import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { SectionHeader } from '../components/SectionHeader';
import { getAvatarImage } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { RichTextEditor } from '../components/RichTextEditor';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Calendar, ArrowLeft, Trash2, Newspaper, Image, MapPin, Heart, Video, BookOpen, Upload, Pencil, MessageCircle, Send } from 'lucide-react';

import { API, mediaUrl } from '../lib/api';
import { stripHtmlPreview } from '../lib/utils';
import { useJazyk } from '../context/LanguageContext';

// Helper: render video from URL (YouTube, Vimeo, or direct file)
const renderVideo = (url, t, className = '') => {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s?]+)/);
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (ytMatch) {
    return (
      <div className={`aspect-video rounded-xl overflow-hidden bg-black ${className}`}>
        <iframe
          src={`https://www.youtube.com/embed/${ytMatch[1]}`}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="YouTube video"
        />
      </div>
    );
  }
  if (vimeoMatch) {
    return (
      <div className={`aspect-video rounded-xl overflow-hidden bg-black ${className}`}>
        <iframe
          src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
          className="w-full h-full"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title="Vimeo video"
        />
      </div>
    );
  }
  return (
    <div className={`rounded-xl overflow-hidden bg-black ${className}`}>
      <video controls className="w-full max-h-[400px]" src={url}>
        <a href={url} className="text-bloom-violet">{t('akt.prehratVideo')}</a>
      </video>
    </div>
  );
};

// Helper: render article content — supports HTML (from rich editor) and plain text
const renderContent = (content, t) => {
  if (!content) return null;
  // If content looks like HTML (starts with tag or has tags)
  const isHtml = /<[a-z][^>]*>/.test(content);
  if (isHtml) {
    return (
      <div
        className="article-content text-sm md:text-base text-bloom-sub leading-relaxed"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }
  // Plain text with inline media detection
  const lines = content.split('\n');
  return lines.map((line, idx) => {
    const trimmed = line.trim();
    if (trimmed.match(/^https?:\/\/\S+$/) && trimmed.length > 10) {
      // Image URL
      if (trimmed.match(/\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i)) {
        return (
          <div key={idx} className="my-4">
            <img src={trimmed} alt={t('akt.obrazekVClanku')} className="max-w-full rounded-xl shadow-sm" />
          </div>
        );
      }
      // YouTube
      const ytMatch = trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s?]+)/);
      if (ytMatch) {
        return (
          <div key={idx} className="aspect-video my-4 rounded-xl overflow-hidden bg-black">
            <iframe
              src={`https://www.youtube.com/embed/${ytMatch[1]}`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="YouTube video"
            />
          </div>
        );
      }
      // Vimeo
      const vimeoMatch = trimmed.match(/vimeo\.com\/(\d+)/);
      if (vimeoMatch) {
        return (
          <div key={idx} className="aspect-video my-4 rounded-xl overflow-hidden bg-black">
            <iframe
              src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
              className="w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title="Vimeo video"
            />
          </div>
        );
      }
      // Direct video file
      if (trimmed.match(/\.(mp4|webm|ogg)(\?.*)?$/i)) {
        return (
          <div key={idx} className="my-4 rounded-xl overflow-hidden bg-black">
            <video controls className="w-full max-h-[400px]" src={trimmed} />
          </div>
        );
      }
    }
    if (!trimmed) return <br key={idx} />;
    return <p key={idx} className="mb-2 leading-relaxed">{line}</p>;
  });
};

const NewsPage = () => {
  const { t, datum, datumCas } = useJazyk();
  const { newsId } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user, isAuthenticated } = useAuth();
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activeCat, setActiveCat] = useState('all');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [newsCat, setNewsCat] = useState('local');
  const [imageFile, setImageFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  // Náhrada, dokud server nevrátí vlastní seznam kategorií.
  const [newsCats, setNewsCats] = useState([
    // „Vše" si přidává aplikace sama, proto se překládá při vykreslení.
    { id: 'all', name: 'Vše' },
    { id: 'zkusenosti', name: 'Zkušenosti komunity' },
    { id: 'world', name: 'Ze světa' },
    { id: 'local', name: 'Domácí' },
    { id: 'tips', name: 'Tipy a triky' },
    { id: 'events', name: 'Eventy' },
    { id: 'interviews', name: 'Rozhovory' },
  ]);

  useEffect(() => {
    fetchNews();
    axios.get(`${API}/news-categories`).then(r => {
      setNewsCats([{ id: 'all', name: 'Vše' }, ...r.data]);
    }).catch(() => {});
  }, []);
  // Sync the open article from the URL (:newsId) — supports direct entry / refresh / sharing.
  useEffect(() => {
    if (!newsId) { setSelected(null); setNotFound(false); return; }
    const found = newsList.find(n => n.id === newsId);
    if (found) { setSelected(found); setNotFound(false); return; }
    if (newsList.length > 0) {
      // Not in the loaded list — fetch the single article directly
      axios.get(`${API}/news/${newsId}`)
        .then(r => { setSelected(r.data); setNotFound(false); })
        .catch(() => { setSelected(null); setNotFound(true); });
    }
  }, [newsId, newsList]);

  const fetchComments = useCallback(async (articleId) => {
    try { const r = await axios.get(`${API}/news/${articleId}/comments`); setComments(r.data); } catch { setComments([]); }
  }, []);

  // Load comments for the currently open article
  useEffect(() => {
    if (newsId) fetchComments(newsId); else setComments([]);
  }, [newsId, fetchComments]);

  const fetchNews = async () => { try { const r = await axios.get(`${API}/news`); setNewsList(r.data); } catch {} finally { setLoading(false); } };

  const handleCreate = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let finalImageUrl = imageUrl;
      let finalVideoUrl = videoUrl;
      
      // Upload image file if selected
      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        const r = await axios.post(`${API}/news/upload-media`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        finalImageUrl = r.data.url;
      }
      // Upload video file if selected
      if (videoFile) {
        const formData = new FormData();
        formData.append('file', videoFile);
        const r = await axios.post(`${API}/news/upload-media`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        finalVideoUrl = r.data.url;
      }
      
      await axios.post(`${API}/news`, { title, content, image_url: finalImageUrl, video_url: finalVideoUrl, thumbnail_url: thumbnailUrl || finalImageUrl, category: newsCat });
      toast.success(t('akt.publikovan'));
      setDialogOpen(false); setTitle(''); setContent(''); setImageUrl(''); setVideoUrl(''); setThumbnailUrl('');
      setImageFile(null); setVideoFile(null);
      fetchNews();
    } catch (e) { toast.error(e.response?.data?.detail || t('akt.chybaPublikovat')); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id, authorId) => {
    if (!window.confirm(t('akt.opravduSmazat'))) return;
    try { await axios.delete(`${API}/news/${id}`); toast.success(t('pod.smazano')); navigate('/news'); fetchNews(); } catch { toast.error(t('akt.chyba')); }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !selected) return;
    setCommentLoading(true);
    try {
      await axios.post(`${API}/news/${selected.id}/comments`, { content: commentText });
      setCommentText('');
      await fetchComments(selected.id);
    } catch (err) { toast.error(err.response?.data?.detail || t('akt.chybaKomentar')); }
    finally { setCommentLoading(false); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/news/comments/${commentId}`);
      if (selected) await fetchComments(selected.id);
    } catch { toast.error(t('akt.chybaSmazatKomentar')); }
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setTitle(item.title || '');
    setContent(item.content || '');
    setImageUrl(item.image_url || '');
    setVideoUrl(item.video_url || '');
    setThumbnailUrl(item.thumbnail_url || '');
    setNewsCat(item.category || 'local');
    setImageFile(null); setVideoFile(null);
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingItem) return;
    setUploading(true);
    try {
      let finalImageUrl = imageUrl;
      let finalVideoUrl = videoUrl;
      if (imageFile) {
        const fd = new FormData(); fd.append('file', imageFile);
        const r = await axios.post(`${API}/news/upload-media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        finalImageUrl = r.data.url;
      }
      if (videoFile) {
        const fd = new FormData(); fd.append('file', videoFile);
        const r = await axios.post(`${API}/news/upload-media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        finalVideoUrl = r.data.url;
      }
      await axios.put(`${API}/news/${editingItem.id}`, { title, content, image_url: finalImageUrl, video_url: finalVideoUrl, thumbnail_url: thumbnailUrl || finalImageUrl, category: newsCat });
      toast.success(t('akt.aktualizovan'));
      setEditDialogOpen(false); setEditingItem(null);
      setTitle(''); setContent(''); setImageUrl(''); setVideoUrl(''); setThumbnailUrl('');
      setImageFile(null); setVideoFile(null);
      await fetchNews();
      // Refresh selected if editing the viewed article
      setSelected(prev => prev?.id === editingItem.id ? { ...prev, title, content, image_url: finalImageUrl, video_url: finalVideoUrl, category: newsCat } : prev);
    } catch (e) { toast.error(e.response?.data?.detail || t('pod.chybaUlozit')); }
    finally { setUploading(false); }
  };

  const activeCatData = newsCats.find(c => c.id === activeCat);
  const allowedRoles = activeCatData?.allowed_roles;
  const canPost = isAuthenticated && user && (
    allowedRoles ? allowedRoles.includes(user.role) : (isAdmin || activeCat === 'zkusenosti')
  );
  const filtered = activeCat === 'all' ? newsList : newsList.filter(n => n.category === activeCat);
  const getAuthorName = (n) => n.is_community_story ? n.author_name : (n.admin_name || n.author_name);

  if (notFound) return (
    <div className="min-h-screen py-6" data-testid="news-not-found">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16">
        <Newspaper className="w-10 h-10 text-bloom-sub/30 mx-auto mb-3" />
        <h1 className="font-serif text-xl font-semibold text-bloom-text mb-3">{t('akt.clanekNenalezen')}</h1>
        <Button variant="ghost" className="text-bloom-violet" onClick={() => navigate('/news')}><ArrowLeft className="w-4 h-4 mr-1"/>{t('akt.zpetNaAktuality')}</Button>
      </div>
    </div>
  );

  if (selected) return (
    <>
    <div className="min-h-screen py-6" data-testid="news-detail">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" onClick={() => navigate('/news')} className="mb-4 text-bloom-violet" data-testid="back-to-news"><ArrowLeft className="w-4 h-4 mr-1"/>{t('akt.zpetNaAktuality')}</Button>
        <article>
          {(selected.thumbnail_url || selected.image_url) && (
            <div className="aspect-video mb-6 rounded-xl overflow-hidden">
              <img src={mediaUrl(selected.thumbnail_url || selected.image_url)} alt={selected.title} className="w-full h-full object-cover"/>
            </div>
          )}
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-bloom-text mb-3">{selected.title}</h1>
          <div className="flex items-center gap-3 text-sm text-bloom-sub mb-6 flex-wrap">
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5"/>{datum(selected.created_at, { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            {selected.author_id ? (
              <Link to={`/users/${selected.author_id}`} className="hover:text-bloom-violet transition-colors" data-testid="news-detail-author-link">{t('akt.autor', { kdo: getAuthorName(selected) })}</Link>
            ) : (
              <span>{t('akt.autor', { kdo: getAuthorName(selected) })}</span>
            )}
            {selected.category && <span className="px-2 py-0.5 rounded-full bg-bloom-violet/10 text-bloom-violet text-xs">{newsCats.find(c=>c.id===selected.category)?.name}</span>}
            {selected.is_community_story && <span className="px-2 py-0.5 rounded-full bg-[#F5A9B8]/20 text-[#F5A9B8] text-xs">{t('akt.osobniPribeh')}</span>}
          </div>
          {selected.video_url && (
            <div className="mb-6">
              {renderVideo(selected.video_url, t)}
            </div>
          )}
          <div className="text-bloom-text">
            {renderContent(selected.content, t)}
          </div>
          {(isAdmin || (user && user.id === selected.author_id)) && (
            <div className="mt-6 pt-6 border-t border-border flex gap-2">
              <Button variant="outline" className="border-bloom-violet/40 text-bloom-violet hover:bg-bloom-violet/10"
                onClick={() => handleOpenEdit(selected)} data-testid="edit-news-btn">
                <Pencil className="w-4 h-4 mr-1.5"/>{t('akt.upravitClanek')}
              </Button>
              <Button variant="destructive" onClick={() => handleDelete(selected.id, selected.author_id)} data-testid="delete-news-btn">
                <Trash2 className="w-4 h-4 mr-1"/>{t('obecne.smazat')}
              </Button>
            </div>
          )}
        </article>

        {/* Comments section */}
        <div className="mt-8" data-testid="news-comments">
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle className="w-4 h-4 text-bloom-violet" />
            <h2 className="font-semibold text-bloom-text text-sm">{t('akt.komentare', { pocet: comments.length })}</h2>
          </div>

          {/* Comment list */}
          <div className="space-y-3 mb-6">
            {comments.length === 0 && (
              <p className="text-sm text-bloom-sub/70 py-3">{t('akt.zadneKomentare')}</p>
            )}
            {comments.map(c => (
              <div key={c.id} className="flex gap-3 group" data-testid={`comment-${c.id}`}>
                <Avatar className="w-8 h-8 shrink-0">
                  <AvatarImage src={getAvatarImage(c.avatar, c.custom_avatar)} />
                  <AvatarFallback className="bg-bloom-pride-pink/30 text-bloom-pride-pink text-[10px]">
                    {c.username?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="bg-white rounded-xl border border-border/50 px-3.5 py-2.5">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Link to={`/users/${c.user_id}`} className="text-xs font-semibold text-bloom-violet hover:underline">
                        {c.username}
                      </Link>
                      <span className="text-[10px] text-bloom-sub/60">
                        {datumCas(c.created_at, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-sm text-bloom-text leading-relaxed">{c.content}</p>
                  </div>
                  {(isAdmin || user?.id === c.user_id) && (
                    <button onClick={() => handleDeleteComment(c.id)}
                      className="mt-1 ml-2 text-[10px] text-bloom-sub/50 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-comment-${c.id}`}>
                      {t('obecne.smazat')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Add comment */}
          {isAuthenticated && user?.email_verified ? (
            <form onSubmit={handleAddComment} className="flex gap-2 items-start" data-testid="comment-form">
              <Avatar className="w-8 h-8 shrink-0 mt-0.5">
                <AvatarImage src={getAvatarImage(user?.avatar, user?.custom_avatar)} />
                <AvatarFallback className="bg-bloom-violet text-white text-[10px]">
                  {user?.username?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 flex gap-2">
                <Input
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  placeholder={t('akt.pridatKomentar')}
                  className="flex-1"
                  data-testid="comment-input"
                />
                <Button type="submit" size="sm" className="bg-bloom-violet text-white shrink-0" disabled={commentLoading || !commentText.trim()}
                  data-testid="comment-submit">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          ) : isAuthenticated ? (
            <p className="text-sm text-amber-600">{t('akt.proKomentovaniOverte')} <Link to="/profile" className="font-medium hover:underline">{t('akt.profil')}</Link></p>
          ) : (
            <p className="text-sm text-bloom-sub">{t('akt.prihlasteSeProKomentovani')}</p>
          )}
        </div>
      </div>
    </div>
    {/* Edit dialog available from detail view */}
    <Dialog open={editDialogOpen} onOpenChange={open => { setEditDialogOpen(open); if (!open) { setEditingItem(null); setTitle(''); setContent(''); setImageUrl(''); setVideoUrl(''); setThumbnailUrl(''); setImageFile(null); setVideoFile(null); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-bloom-text">{t('akt.upravitClanek')}</DialogTitle>
          <DialogDescription>{t('akt.upravteObsah')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleUpdate} className="space-y-4 pt-2">
          <div>
            <Label>{t('akt.nadpisP')}</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} required data-testid="edit-news-title" />
          </div>
          <div>
            <Label>{t('prav.kategorie')}</Label>
            <Select value={newsCat} onValueChange={setNewsCat}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{newsCats.filter(c => c.id !== 'all').map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('akt.obsahP')}</Label>
            <RichTextEditor value={content} onChange={setContent} placeholder={t('akt.textClanku')} data-testid="edit-news-content" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1"><Image className="w-3.5 h-3.5"/>{t('akt.obrazekUrlNeboSoubor')}</Label>
              <Input value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://..." />
              <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files[0])} className="mt-1 text-xs" />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Video className="w-3.5 h-3.5"/>{t('akt.videoUrlNeboSoubor')}</Label>
              <Input value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="YouTube / Vimeo / URL" />
              <input type="file" accept="video/*" onChange={e => setVideoFile(e.target.files[0])} className="mt-1 text-xs" />
            </div>
          </div>
          <Button type="submit" className="w-full bg-bloom-violet text-white" disabled={uploading} data-testid="edit-news-submit">
            {t(uploading ? 'akt.ukladam' : 'pod.ulozitZmeny')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
    </>
  );

  return (
    <>
    <div className="min-h-screen py-6" data-testid="news-page">
      <div className="pride-bar mb-6"/>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <SectionHeader sectionKey="news" klicTitulku="nav.news" klicPodtitulku="akt.podtitulek" defaultColor="#5BCEFA" />
          </div>
          {canPost && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-bloom-mint text-white" data-testid="create-news-btn" onClick={() => { if(!isAdmin) setNewsCat(activeCat !== 'all' ? activeCat : 'zkusenosti'); }}>
                  <Plus className="w-4 h-4 mr-1"/>
                  {t(isAdmin ? 'akt.novaAktualita' : 'akt.sdiletPribeh')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-serif text-bloom-text">
                    {t(isAdmin ? 'akt.vytvoritAktualitu' : 'akt.sdiletZkusenost')}
                  </DialogTitle>
                  <DialogDescription>{t(isAdmin ? 'akt.napisteAktualitu' : 'akt.podelteSe')}</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div><Label className="text-bloom-text">{t('akt.nadpis')}</Label><Input value={title} onChange={(e)=>setTitle(e.target.value)} required data-testid="news-title-input"/></div>
                  {isAdmin && (
                    <div><Label className="text-bloom-text">{t('prav.kategorie')}</Label>
                      <Select value={newsCat} onValueChange={setNewsCat}>
                        <SelectTrigger><SelectValue/></SelectTrigger>
                        <SelectContent>{newsCats.filter(c=>c.id!=='all').map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  {!isAdmin && (() => {
                    const myCats = newsCats.filter(c => c.id !== 'all' && c.allowed_roles?.includes(user?.role));
                    return myCats.length > 1 ? (
                      <div><Label className="text-bloom-text">{t('prav.kategorie')}</Label>
                        <Select value={newsCat} onValueChange={setNewsCat}>
                          <SelectTrigger><SelectValue/></SelectTrigger>
                          <SelectContent>{myCats.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    ) : null;
                  })()}
                  <div><Label className="text-bloom-text">{t('akt.obrazek')}</Label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-bloom-violet/30 rounded-lg cursor-pointer hover:bg-bloom-violet/5 transition-colors" data-testid="news-image-upload">
                        <Upload className="w-4 h-4 text-bloom-violet" />
                        <span className="text-sm text-bloom-sub">{imageFile ? imageFile.name : t('akt.nahratObrazek')}</span>
                        <input type="file" accept="image/*" className="hidden" onChange={e => { setImageFile(e.target.files[0]); setImageUrl(''); }} />
                      </label>
                      <div className="flex items-center gap-2 text-xs text-bloom-sub">
                        <span>{t('akt.neboUrl')}</span>
                        <Input value={imageUrl} onChange={(e) => { setImageUrl(e.target.value); setImageFile(null); }} className="flex-1 h-8 text-xs" placeholder="https://..." data-testid="news-image-input" />
                      </div>
                    </div>
                  </div>
                  <div><Label className="text-bloom-text">Video</Label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-bloom-violet/30 rounded-lg cursor-pointer hover:bg-bloom-violet/5 transition-colors" data-testid="news-video-upload">
                        <Upload className="w-4 h-4 text-bloom-violet" />
                        <span className="text-sm text-bloom-sub">{videoFile ? videoFile.name : t('akt.nahratVideo')}</span>
                        <input type="file" accept="video/*" className="hidden" onChange={e => { setVideoFile(e.target.files[0]); setVideoUrl(''); }} />
                      </label>
                      <div className="flex items-center gap-2 text-xs text-bloom-sub">
                        <span>{t('akt.neboUrl')}</span>
                        <Input value={videoUrl} onChange={(e) => { setVideoUrl(e.target.value); setVideoFile(null); }} className="flex-1 h-8 text-xs" placeholder="https://youtube.com/..." data-testid="news-video-input" />
                      </div>
                    </div>
                  </div>
                  <div><Label className="text-bloom-text">{t('akt.obsahP')}</Label>
                    <RichTextEditor
                      value={content}
                      onChange={v => setContent(v)}
                      rows={8}
                      placeholder={t(isAdmin ? 'akt.textAktuality' : 'akt.sdilejtePribeh')}
                    />
                  </div>
                  <Button type="submit" className="w-full bg-bloom-violet text-white" data-testid="news-submit-btn" disabled={uploading}>
                    {uploading ? t('akt.nahravam') : t(isAdmin ? 'akt.publikovat' : 'akt.sdiletPribeh')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 mb-6">
          {newsCats.map(c=>(
            <button key={c.id} onClick={()=>setActiveCat(c.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeCat===c.id?'bg-bloom-violet text-white shadow-sm':'bg-white border border-border text-bloom-sub hover:border-bloom-violet/50 hover:text-bloom-violet'}`}
              data-testid={`news-cat-${c.id}`}>{c.id === 'all' ? t('obecne.vse') : c.name}</button>
          ))}
        </div>

        {activeCat === 'zkusenosti' && (
          <div className="mb-5 p-4 bg-[#F5A9B8]/10 border border-[#F5A9B8]/30 rounded-xl flex items-start gap-3" data-testid="zkusenosti-note">
            <Heart className="w-4 h-4 text-[#F5A9B8] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-bloom-sub leading-relaxed">
                <strong className="text-bloom-text">{t('akt.zkusenostiTucne')}</strong>{' '}
                {t('akt.zkusenostiPopis')}
              </p>
              {isAuthenticated && !canPost && activeCat === 'zkusenosti' && (
                <Button size="sm" className="mt-2 bg-[#F5A9B8] text-white text-xs" onClick={() => { setNewsCat('zkusenosti'); setDialogOpen(true); }}>
                  <BookOpen className="w-3.5 h-3.5 mr-1"/>{t('akt.sdiletSvujPribeh')}
                </Button>
              )}
            </div>
          </div>
        )}

        {loading?<div className="flex justify-center py-12"><div className="spinner"/></div>:filtered.length===0?(
          <Card className="bg-white border-border/50"><CardContent className="p-12 text-center"><Newspaper className="w-10 h-10 text-bloom-sub/30 mx-auto mb-3"/><h3 className="font-serif text-lg font-semibold text-bloom-text mb-1">{t('akt.zadneAktuality')}</h3><p className="text-sm text-bloom-sub">{t('akt.zadneVKategorii')}</p></CardContent></Card>
        ):(
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {filtered.map((n,i)=>(
              <Card key={n.id} className={`bg-white border-border/50 card-hover cursor-pointer overflow-hidden ${i===0?'md:col-span-2':''}`} onClick={()=>navigate(`/news/${n.id}`)} data-testid={`news-card-${n.id}`}>
                <CardContent className="p-0">
                  {/* Show thumbnail on desktop always; on mobile only for featured card */}
                  {(n.thumbnail_url||n.image_url)&&(
                    <div className={`overflow-hidden ${i===0?'aspect-[21/9]':'aspect-video hidden md:block'}`}>
                      <img src={mediaUrl(n.thumbnail_url||n.image_url)} alt={n.title} className="w-full h-full object-cover"/>
                    </div>
                  )}
                  <div className="p-3 md:p-4">
                    <div className="flex items-center gap-1.5 text-xs text-bloom-sub mb-1.5 flex-wrap">
                      <Calendar className="w-3 h-3 hidden sm:block"/><span className="hidden sm:inline">{datum(n.created_at)}</span>
                      {n.category&&<span className="px-2 py-0.5 rounded-full bg-bloom-violet/10 text-bloom-violet">{newsCats.find(c=>c.id===n.category)?.name}</span>}
                      {n.is_community_story && <span className="px-2 py-0.5 rounded-full bg-[#F5A9B8]/20 text-[#F5A9B8]">{t('akt.osobniPribeh')}</span>}
                      {n.video_url && <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-600 flex items-center gap-0.5"><Video className="w-3 h-3"/>Video</span>}
                    </div>
                    <h2 className={`font-serif font-semibold text-bloom-text mb-1 leading-snug ${i===0?'text-lg md:text-xl':'text-sm md:text-base'}`}>{n.title}</h2>
                    <p className={`text-xs md:text-sm text-bloom-sub line-clamp-1 md:line-clamp-2`}>{stripHtmlPreview(n.content, i===0?200:100)}...</p>
                    <p className="text-xs text-bloom-sub/60 mt-1.5 hidden sm:block">
                      {n.author_id ? (
                        <Link to={`/users/${n.author_id}`} className="hover:text-bloom-violet transition-colors" onClick={e => e.stopPropagation()} data-testid={`news-card-author-link-${n.id}`}>{t('akt.autor', { kdo: getAuthorName(n) })}</Link>
                      ) : (
                        <span>{t('akt.autor', { kdo: getAuthorName(n) })}</span>
                      )}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>

    {/* Edit article dialog (admin or author) */}
    <Dialog open={editDialogOpen} onOpenChange={open => { setEditDialogOpen(open); if (!open) { setEditingItem(null); setTitle(''); setContent(''); setImageUrl(''); setVideoUrl(''); setThumbnailUrl(''); setImageFile(null); setVideoFile(null); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-bloom-text">{t('akt.upravitClanek')}</DialogTitle>
          <DialogDescription>{t('akt.upravteObsah')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleUpdate} className="space-y-4 pt-2">
          <div>
            <Label>{t('akt.nadpisP')}</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} required data-testid="edit-news-title" />
          </div>
          <div>
            <Label>{t('prav.kategorie')}</Label>
            <Select value={newsCat} onValueChange={setNewsCat}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{newsCats.filter(c => c.id !== 'all').map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('akt.obsahP')}</Label>
            <RichTextEditor value={content} onChange={setContent} placeholder={t('akt.textClanku')} data-testid="edit-news-content" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1"><Image className="w-3.5 h-3.5"/>{t('akt.obrazekUrlNeboSoubor')}</Label>
              <Input value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://..." />
              <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files[0])} className="mt-1 text-xs" />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Video className="w-3.5 h-3.5"/>{t('akt.videoUrlNeboSoubor')}</Label>
              <Input value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="YouTube / Vimeo / URL" />
              <input type="file" accept="video/*" onChange={e => setVideoFile(e.target.files[0])} className="mt-1 text-xs" />
            </div>
          </div>
          <Button type="submit" className="w-full bg-bloom-violet text-white" disabled={uploading} data-testid="edit-news-submit">
            {t(uploading ? 'akt.ukladam' : 'pod.ulozitZmeny')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  </>
  );
};

export default NewsPage;
